/**
 * Editors for a caption word's LOOK (font, weight, italic, fill, outline, glow,
 * pill) and MOTION (entrance as it's spoken + a hold motion). Shared by the
 * Emphasis tab (every key word) and the word inspector (one word / a selection).
 * `onChange` receives a patch; a `null` value clears that field so it falls back
 * to the next layer (emphasis → caption).
 */
import {
  MOTION_KIND_DEFAULTS,
  type GradientSpec,
  type HookIdle,
  type HookMotionKind,
  type StrokeSpec,
  type WordFill,
  type WordGlow,
  type WordMotionSpec,
} from '@captionflow/caption-engine';
import { type ReactNode } from 'react';
import { availableFontWeight, FONT_REGISTRY } from '../fontList';
import {
  IDLES,
  IN_KINDS,
  WEIGHTS,
  PhaseControls,
  Row,
  Seg,
  Slider,
  Toggle,
  hex,
  nearestWeight,
  pct,
} from './controls';

export interface WordLookValue {
  fontFamily?: string | null;
  fontWeight?: number | null;
  italic?: boolean | null;
  fill?: WordFill | null;
  gradient?: GradientSpec | null;
  stroke?: StrokeSpec | null;
  glow?: WordGlow | null;
  highlightColor?: string | null;
}
export type WordLookPatch = { [K in keyof WordLookValue]?: WordLookValue[K] | null };

const FILLS: { v: string; label: string }[] = [
  { v: '', label: 'Default' },
  { v: 'gradient', label: 'Gradient' },
  { v: 'metal', label: 'Metal' },
];

export function WordLookEditor({
  value,
  onChange,
  inheritFont,
  showPill,
  grouped = false,
  typographyExtra,
  inheritFamily,
  inheritWeight,
  typographyCustom = false,
}: {
  value: WordLookValue;
  onChange: (p: WordLookPatch) => void;
  /** Label for "no own font" (e.g. "Caption font"). */
  inheritFont: string;
  /** Show the per-word pill control (the emphasis tab has its own pill). */
  showPill?: boolean;
  grouped?: boolean;
  typographyExtra?: ReactNode;
  inheritFamily?: string;
  inheritWeight?: number;
  typographyCustom?: boolean;
}) {
  const effectiveFamily = value.fontFamily ?? inheritFamily ?? 'Inter';
  const weights = FONT_REGISTRY.find((font) => font.family === effectiveFamily)?.weights ?? [700];
  const weight = availableFontWeight(effectiveFamily, value.fontWeight ?? inheritWeight ?? 700);
  const outlineMode = value.stroke == null ? '' : value.stroke.widthPx > 0 ? 'on' : 'none';
  return (
    <>
      <LookGroup
        grouped={grouped}
        label="Typography"
        custom={
          typographyCustom ||
          value.fontFamily != null ||
          value.fontWeight != null ||
          value.italic != null
        }
      >
        <Row label="Font">
          <select
            className="ses-select"
            value={value.fontFamily ?? ''}
            onChange={(e) => onChange({ fontFamily: e.target.value || null })}
          >
            <option value="">{inheritFont}</option>
            <FontOptions />
          </select>
        </Row>
        {grouped ? (
          <>
            <Row label="Weight">
              <div className="shk-size">
                <input
                  type="range"
                  aria-label="Word font weight"
                  aria-valuetext={String(weight)}
                  min={0}
                  max={Math.max(1, weights.length - 1)}
                  step={1}
                  value={Math.max(0, weights.indexOf(weight))}
                  disabled={weights.length === 1}
                  onChange={(e) => onChange({ fontWeight: weights[Number(e.target.value)]! })}
                />
                <span>{weight}</span>
              </div>
            </Row>
            {value.fontWeight != null && (
              <button className="si-linkbtn" onClick={() => onChange({ fontWeight: null })}>
                Use caption weight
              </button>
            )}
          </>
        ) : (
          <>
            <Row label="Weight">
              <Seg
                options={[{ v: '', label: '—' }, ...WEIGHTS]}
                value={value.fontWeight ? String(nearestWeight(value.fontWeight)) : ''}
                onPick={(v) => onChange({ fontWeight: v ? Number(v) : null })}
              />
            </Row>
          </>
        )}
        <Row label="Italic">
          <Toggle
            on={Boolean(value.italic)}
            onClick={() => onChange({ italic: value.italic ? null : true })}
          />
        </Row>
        {typographyExtra}
      </LookGroup>
      <LookGroup
        grouped={grouped}
        label="Fill & effects"
        custom={
          value.fill != null ||
          value.stroke != null ||
          value.glow != null ||
          value.highlightColor != null
        }
      >
        <Row label="Fill">
          <Seg
            options={FILLS}
            value={value.fill && value.fill !== 'solid' ? value.fill : ''}
            onPick={(v) =>
              onChange(
                v === 'gradient'
                  ? {
                      fill: 'gradient',
                      gradient: value.gradient ?? { from: '#ffe16b', to: '#ff5d8f', angleDeg: 90 },
                    }
                  : { fill: (v || null) as WordFill | null },
              )
            }
          />
        </Row>
        {value.fill === 'gradient' && (
          <Row label="Gradient">
            <div className="shk-inline">
              <input
                type="color"
                className="shk-color"
                value={hex(value.gradient?.from, '#ffe16b')}
                onChange={(e) =>
                  onChange({
                    gradient: {
                      ...(value.gradient ?? { to: '#ff5d8f', angleDeg: 90 }),
                      from: e.target.value,
                    },
                  })
                }
              />
              <input
                type="color"
                className="shk-color"
                value={hex(value.gradient?.to, '#ff5d8f')}
                onChange={(e) =>
                  onChange({
                    gradient: {
                      ...(value.gradient ?? { from: '#ffe16b', angleDeg: 90 }),
                      to: e.target.value,
                    },
                  })
                }
              />
            </div>
          </Row>
        )}
        <Row label="Outline">
          <Seg
            options={[
              { v: '', label: 'Default' },
              { v: 'on', label: 'Custom' },
              { v: 'none', label: 'None' },
            ]}
            value={outlineMode}
            onPick={(v) =>
              onChange({
                stroke:
                  v === ''
                    ? null
                    : v === 'none'
                      ? { color: '#000000', widthPx: 0 }
                      : {
                          color: value.stroke?.color ?? '#000000',
                          widthPx: value.stroke?.widthPx || 8,
                        },
              })
            }
          />
        </Row>
        {outlineMode === 'on' && value.stroke && (
          <>
            <Row label="Outline color">
              <input
                type="color"
                className="shk-color"
                value={hex(value.stroke.color, '#000000')}
                onChange={(e) => onChange({ stroke: { ...value.stroke!, color: e.target.value } })}
              />
            </Row>
            <Slider
              label="Outline width"
              min={1}
              max={24}
              step={1}
              value={value.stroke.widthPx}
              onChange={(v) => onChange({ stroke: { ...value.stroke!, widthPx: v } })}
              display={`${value.stroke.widthPx}`}
            />
          </>
        )}
        <Row label="Glow">
          <Toggle
            on={Boolean(value.glow)}
            onClick={() => onChange({ glow: value.glow ? null : { color: '#22d3ee', blurPx: 24 } })}
          />
        </Row>
        {value.glow && (
          <>
            <Row label="Glow color">
              <input
                type="color"
                className="shk-color"
                value={hex(value.glow.color, '#22d3ee')}
                onChange={(e) => onChange({ glow: { ...value.glow!, color: e.target.value } })}
              />
            </Row>
            <Slider
              label="Glow size"
              min={4}
              max={80}
              step={1}
              value={value.glow.blurPx}
              onChange={(v) => onChange({ glow: { ...value.glow!, blurPx: v } })}
              display={`${value.glow.blurPx}`}
            />
          </>
        )}
        {showPill && (
          <>
            <Row label="Background">
              <Toggle
                on={Boolean(value.highlightColor)}
                onClick={() =>
                  onChange({ highlightColor: value.highlightColor ? null : '#101216' })
                }
              />
            </Row>
            {value.highlightColor && (
              <Row label="Background color">
                <input
                  type="color"
                  className="shk-color"
                  value={hex(value.highlightColor, '#101216')}
                  onChange={(e) => onChange({ highlightColor: e.target.value })}
                />
              </Row>
            )}
          </>
        )}
      </LookGroup>
    </>
  );
}

function LookGroup({
  grouped,
  label,
  custom,
  children,
}: {
  grouped: boolean;
  label: string;
  custom: boolean;
  children: ReactNode;
}) {
  return grouped ? (
    <details className="si-group">
      <summary>
        {label}
        {custom && <span className="si-custom">Custom</span>}
      </summary>
      <div className="si-group-body">{children}</div>
    </details>
  ) : (
    <>{children}</>
  );
}

function FontOptions() {
  return (
    <>
      {FONT_REGISTRY.map((f) => (
        <option key={f.family} value={f.family}>
          {f.displayName}
        </option>
      ))}
    </>
  );
}

/** Entrance-as-spoken + hold motion for a word. `null` = off / follow default. */
const STILL_MOTION: WordMotionSpec = {
  in: { kind: 'none', durationMs: 0, distance: 0, blur: 0, easing: 'linear' },
  hold: 'none',
  holdAmount: 0,
};

export function WordMotionEditor({
  value,
  onChange,
  offLabel,
  allowStill,
}: {
  value: WordMotionSpec | null | undefined;
  onChange: (m: WordMotionSpec | null) => void;
  /** Label for "no motion of its own" (e.g. "Off" or "Default"). */
  offLabel: string;
  /** Offer "Still": an explicit no-motion that overrides a default motion. */
  allowStill?: boolean;
}) {
  const still = Boolean(value && value.in.kind === 'none' && value.hold === 'none');
  const kind: HookMotionKind | '' = value?.in.kind && value.in.kind !== 'none' ? value.in.kind : '';
  const set = (patch: Partial<WordMotionSpec>) =>
    onChange({
      in: value?.in ?? { kind: 'none', durationMs: 0, distance: 0, blur: 0, easing: 'linear' },
      hold: value?.hold ?? 'none',
      holdAmount: value?.holdAmount ?? 0.5,
      ...patch,
    });
  const pickKind = (k: HookMotionKind | '' | 'still') => {
    if (k === 'still') return onChange(STILL_MOTION);
    if (!k) {
      // Keep a hold motion if one is set; otherwise clear the motion entirely.
      if (value && value.hold !== 'none')
        set({ in: { kind: 'none', durationMs: 0, distance: 0, blur: 0, easing: 'linear' } });
      else onChange(null);
      return;
    }
    set({
      in: {
        kind: k,
        ...MOTION_KIND_DEFAULTS[k],
        durationMs: Math.max(250, value?.in.durationMs || 450),
        ...(k.startsWith('slide') ? { distance: 0.05 } : {}),
      },
    });
  };
  return (
    <>
      <Row label="Entrance">
        <Seg
          options={[
            { v: '' as HookMotionKind | '' | 'still', label: offLabel },
            ...(allowStill ? [{ v: 'still' as const, label: 'Still' }] : []),
            ...IN_KINDS.filter((k) => k.v !== 'none'),
          ]}
          value={still ? 'still' : kind}
          onPick={pickKind}
          wrap
        />
      </Row>
      {kind && value && (
        <PhaseControls
          phase={value.in}
          kind={kind}
          onChange={(p) => set({ in: { ...value.in, ...p } })}
        />
      )}
      <Row label="While on screen">
        <Seg
          options={IDLES}
          value={value?.hold ?? 'none'}
          onPick={(v: HookIdle) => (v === 'none' && !kind ? onChange(null) : set({ hold: v }))}
          wrap
        />
      </Row>
      {value && value.hold !== 'none' && (
        <Slider
          label="Amount"
          min={0}
          max={1}
          step={0.02}
          value={value.holdAmount}
          onChange={(v) => set({ holdAmount: v })}
          display={pct(value.holdAmount)}
        />
      )}
    </>
  );
}
